import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { FileText, Camera, ExternalLink, Pencil, AlertTriangle } from "lucide-react";

const DEFAULT_FORM_VERSION = "25/09/2026";
const MEDIA_DECISIONS = [
  ["realizzazione", "Realizzazione foto e video"],
  ["pubblicazione", "Pubblicazione organica"],
  ["pubblicitario", "Utilizzo pubblicitario"],
  ["audio", "Registrazioni audio"],
];

const DOC_TYPES = [
  { type: "affidamento", label: "Modulo affidamento / presa in custodia", icon: FileText,
    statuses: ["Non registrato", "Firmato", "Non firmato"], kind: "custody" },
  { type: "media_consent", label: "Consenso utilizzo foto, video e audio", icon: Camera,
    statuses: ["Non registrato", "Modulo firmato", "Revocato"], kind: "media" },
];

const OK = { Firmato: true, "Modulo firmato": true, "Consenso acquisito": true };
const BAD = { Revocato: true };
const badgeCls = (s) => OK[s] ? "bg-[#E8F5E9] text-[#1E5E2B] border-[#A5D6A7]"
  : BAD[s] ? "bg-[#FFEBEE] text-[#C62828] border-[#EF9A9A]"
  : "bg-[#F5F5F5] text-[#616161] border-[#E0E0E0]";
const safeUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u.trim());
// Legacy media record: had old Foto/Video/Audio flags but no new 4-decision structure.
const isLegacyMedia = (rec) => rec && rec.document_type === "media_consent" && !rec.decisions && (rec.scopes || rec.status === "Consenso acquisito");
const displayStatus = (rec, def) => {
  const s = rec?.status || "Non registrato";
  if (def.kind === "media" && s === "Consenso acquisito") return "Modulo firmato";  // legacy display only
  return s;
};

const DocEditDialog = ({ open, onOpenChange, ownerId, def, record, dogs, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const legacyStatus = def.kind === "media" && record?.status === "Consenso acquisito" ? "Modulo firmato" : (record?.status || "Non registrato");
    setForm({
      document_type: def.type,
      status: legacyStatus,
      signed_at: record?.signed_at || "",
      consent_date: record?.consent_date || "",
      revoked_at: record?.revoked_at || "",
      version_label: record?.version_label || (def.kind === "media" ? DEFAULT_FORM_VERSION : ""),
      notes: record?.notes || "",
      drive_url: record?.drive_url || "",
      associated_dog_ids: record?.associated_dog_ids || [],
      // Never infer new decisions from legacy flags — start blank unless already stored.
      decisions: record?.decisions || { realizzazione: "", pubblicazione: "", pubblicitario: "", audio: "" },
    });
  }, [open, def, record]);

  const f = form;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setDecision = (k, v) => setForm((p) => ({ ...p, decisions: { ...(p.decisions || {}), [k]: v } }));
  const toggleDog = (id) => setForm((p) => {
    const has = (p.associated_dog_ids || []).includes(id);
    return { ...p, associated_dog_ids: has ? p.associated_dog_ids.filter((x) => x !== id) : [...(p.associated_dog_ids || []), id] };
  });

  const save = async () => {
    if (!f) return;
    if (f.drive_url && !safeUrl(f.drive_url)) return toast.error("Il link Drive deve iniziare con http:// o https://");
    const mediaSigned = def.kind === "media" && f.status === "Modulo firmato";
    if (mediaSigned && MEDIA_DECISIONS.some(([k]) => !f.decisions?.[k])) {
      return toast.error("Seleziona esplicitamente tutte e quattro le decisioni del modulo.");
    }
    setSaving(true);
    try {
      const payload = { ...f };
      payload.scopes = null; // deprecated: new schema uses decisions
      if (!(def.kind === "media" && f.status !== "Non registrato")) payload.decisions = null;
      if (record) await api.put(`/owners/${ownerId}/documents/${record.id}`, payload);
      else await api.post(`/owners/${ownerId}/documents`, payload);
      toast.success("Documento aggiornato");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto wg-scroll">
        <DialogHeader><DialogTitle className="font-head text-base">{def.label}</DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-3">
            {def.kind === "media" && isLegacyMedia(record) && (
              <p className="text-xs rounded-lg bg-[#FFF8E1] border border-[#FFE082] text-[#8D6E63] p-2 flex items-start gap-1" data-testid="doc-legacy-warning">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Consenso precedente da riallineare al nuovo modulo: seleziona le quattro decisioni per salvare.
              </p>
            )}
            <div className="space-y-1"><Label>Stato</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid={`doc-${def.type}-status`}><SelectValue /></SelectTrigger>
                <SelectContent>{def.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {def.kind === "media" && f.status === "Modulo firmato" && (
              <div className="space-y-2 rounded-xl border border-border p-3 bg-secondary/40" data-testid="doc-media_consent-decisions">
                <Label>Decisioni del modulo (obbligatorie)</Label>
                {MEDIA_DECISIONS.map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between gap-2">
                    <span className="text-sm">{l}</span>
                    <div className="flex gap-1 shrink-0">
                      {["Acconsento", "Non acconsento"].map((opt) => (
                        <button key={opt} type="button" data-testid={`doc-decision-${k}-${opt === "Acconsento" ? "yes" : "no"}`}
                          onClick={() => setDecision(k, opt)}
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${f.decisions?.[k] === opt ? (opt === "Acconsento" ? "bg-[#1E5E2B] text-white border-[#1E5E2B]" : "bg-[#C62828] text-white border-[#C62828]") : "bg-card border-border"}`}>{opt}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {def.kind === "media" && f.status === "Revocato" && (
              <div className="space-y-2 rounded-xl border border-border p-3 bg-secondary/40" data-testid="doc-media_consent-decisions-readonly">
                <Label>Decisioni del modulo (revocato · sola lettura)</Label>
                {isLegacyMedia(record) || !record?.decisions || MEDIA_DECISIONS.every(([k]) => !record.decisions[k]) ? (
                  <p className="text-xs text-[#8D6E63]" data-testid="doc-revoked-legacy">Consenso precedente senza le quattro decisioni registrate: nessuna scelta storica da mostrare.</p>
                ) : MEDIA_DECISIONS.map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between gap-2">
                    <span className="text-sm">{l}</span>
                    <span data-testid={`doc-revoked-${k}`} className={`text-xs font-semibold ${record.decisions[k] === "Acconsento" ? "text-[#1E5E2B]" : "text-[#C62828]"}`}>{record.decisions[k] || "—"}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {def.kind === "custody" ? (
                <div className="space-y-1"><Label>Data firma</Label>
                  <Input type="date" data-testid={`doc-${def.type}-signed`} value={f.signed_at || ""} onChange={(e) => set("signed_at", e.target.value)} /></div>
              ) : (
                <div className="space-y-1"><Label>Data firma / consenso</Label>
                  <Input type="date" data-testid={`doc-${def.type}-consent`} value={f.consent_date || ""} onChange={(e) => set("consent_date", e.target.value)} /></div>
              )}
              {BAD[f.status] && (
                <div className="space-y-1"><Label>Data revoca</Label>
                  <Input type="date" data-testid={`doc-${def.type}-revoked`} value={f.revoked_at || ""} onChange={(e) => set("revoked_at", e.target.value)} /></div>
              )}
              <div className="space-y-1"><Label>Versione modulo</Label>
                <Input data-testid={`doc-${def.type}-version`} value={f.version_label || ""} onChange={(e) => set("version_label", e.target.value)} /></div>
            </div>

            <div className="space-y-1"><Label>Link documento Drive</Label>
              <Input data-testid={`doc-${def.type}-drive`} value={f.drive_url || ""} onChange={(e) => set("drive_url", e.target.value)} placeholder="https://drive.google.com/…" /></div>

            {dogs?.length > 0 && (
              <div className="space-y-1"><Label>Cani associati (opzionale)</Label>
                <div className="flex flex-wrap gap-2">
                  {dogs.map((d) => (
                    <button key={d.id} type="button" onClick={() => toggleDog(d.id)}
                      className={`max-w-full text-left break-words [overflow-wrap:anywhere] rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${(f.associated_dog_ids || []).includes(d.id) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>{d.nome}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1"><Label>Note</Label>
              <Textarea data-testid={`doc-${def.type}-notes`} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid={`doc-${def.type}-save`} onClick={save} disabled={saving || !f}>{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OwnerDocuments = ({ ownerId, documents = [], dogs = [], onChanged }) => {
  const [editDef, setEditDef] = useState(null);
  const byType = (t) => documents.find((d) => d.document_type === t);

  return (
    <section data-testid="owner-documents">
      <h2 className="font-head text-lg font-bold mb-2">Documenti e consensi</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {DOC_TYPES.map((def) => {
          const rec = byType(def.type);
          const status = displayStatus(rec, def);
          const Icon = def.icon;
          const dec = rec?.decisions || {};
          const signed = def.kind === "media" && status === "Modulo firmato";
          const legacy = isLegacyMedia(rec);
          return (
            <div key={def.type} className="rounded-2xl border border-border bg-card p-4" data-testid={`doc-card-${def.type}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-secondary shrink-0"><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{def.label}</div>
                    <span className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${badgeCls(status)}`} data-testid={`doc-status-${def.type}`}>{status}</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full shrink-0" data-testid={`doc-edit-${def.type}`} onClick={() => setEditDef(def)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                {def.kind === "custody" && rec?.signed_at && <div>Firma: {fmtDate(rec.signed_at)}</div>}
                {def.kind === "media" && rec?.consent_date && <div>Firma/consenso: {fmtDate(rec.consent_date)}</div>}
                {BAD[status] && rec?.revoked_at && <div>Revoca: {fmtDate(rec.revoked_at)}</div>}
                {legacy && <div className="text-[#8D6E63] font-semibold">Consenso precedente da riallineare al nuovo modulo</div>}
                {((signed) || (def.kind === "media" && status === "Revocato")) && !legacy && rec?.decisions && MEDIA_DECISIONS.map(([k, l]) => (
                  <div key={k}>{l}: <b className={dec[k] === "Acconsento" ? "text-[#1E5E2B]" : "text-[#C62828]"}>{dec[k] || "—"}</b></div>
                ))}
                {rec?.version_label && <div>Versione: {rec.version_label}</div>}
                {rec?.notes && <div className="break-words">Note: {rec.notes}</div>}
              </div>
              {safeUrl(rec?.drive_url) && (
                <a href={rec.drive_url} target="_blank" rel="noreferrer" data-testid={`doc-open-${def.type}`}
                  className="mt-2 inline-flex items-center gap-1 text-sm text-primary font-semibold"><ExternalLink className="h-4 w-4" />Apri documento</a>
              )}
            </div>
          );
        })}
      </div>
      {editDef && (
        <DocEditDialog open={!!editDef} onOpenChange={(v) => !v && setEditDef(null)}
          ownerId={ownerId} def={editDef} record={byType(editDef.type)} dogs={dogs} onSaved={onChanged} />
      )}
    </section>
  );
};
